import React from "react";
import { Card } from "./ui/card";
import { Dumbbell } from "lucide-react";

const GREEN = "#10B981";

/**
 * Issue #461 — the drills a coach has written FOR this player, rendered on
 * the Assigned tab of Training & Guides, under the assigned guides.
 *
 * Display-only, deliberately: the list is fetched by the page
 * (`LearningHub.jsx`, the same effect shape as the assigned guides) and
 * this component decides nothing — no API calls, no permissions, no clock.
 *
 * A drill has NO page of its own, unlike a guide: the coach's text IS the
 * content, so the whole body renders here, pre-wrap, on the card. That is
 * also why `coach_drill` notifications land on this tab and why nothing
 * links onward from these cards.
 *
 * Renders nothing at all when there are no drills — the tab's shared empty
 * state ("Nothing assigned yet…") is owned by the page, which shows it
 * only when guides AND drills are both absent.
 */
export default function AssignedDrillsList({ drills }) {
  if (!drills || drills.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="assigned-drills-list">
      <div className="flex items-center gap-2 text-slate-500 text-sm pt-2">
        <Dumbbell size={14} color={GREEN} /> Drills your coaches have written for you.
      </div>
      {drills.map((d) => (
        <Card key={d.assignment_id} className="border-slate-200" data-testid={`assigned-drill-${d.assignment_id}`}>
          <div className="p-4">
            <div className="font-semibold text-slate-900">{d.title}</div>
            {/* The attribution — the same acceptance criterion as the
                assigned guides' "Assigned by" line (#382). */}
            <div
              className="text-[11px] mt-1 font-medium"
              style={{ color: GREEN }}
              data-testid={`assigned-drill-by-${d.assignment_id}`}
            >
              Assigned by {d.assigned_by_coach_name}
              {fmtWhen(d.assigned_at) && ` · ${fmtWhen(d.assigned_at)}`}
            </div>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{d.body}</p>
          </div>
        </Card>
      ))}
    </div>
  );
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}
